import { useState, useEffect, useCallback } from "react";

const RECENT_SEARCHES_KEY = "rpforms_recent_searches";
const MAX_RECENT_SEARCHES = 5;

export interface RecentSearch {
    query: string;
    timestamp: number;
}

export const useRecentSearches = () => {
    const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

    // Load recent searches from localStorage on mount
    useEffect(() => {
        try {
            const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
            if (stored) {
                const parsed: RecentSearch[] = JSON.parse(stored);
                // Sort by timestamp (most recent first) and validate data
                const validSearches = parsed
                    .filter(search => search.query && typeof search.timestamp === 'number')
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, MAX_RECENT_SEARCHES);
                setRecentSearches(validSearches);
            }
        } catch (error) {
            console.warn("Failed to load recent searches from localStorage:", error);
            localStorage.removeItem(RECENT_SEARCHES_KEY);
        }
    }, []);

    const saveToStorage = useCallback((searches: RecentSearch[]) => {
        try {
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
        } catch (error) {
            console.warn("Failed to save recent searches to localStorage:", error);
        }
    }, []);

    const addRecentSearch = useCallback((query: string) => {
        if (!query.trim()) return;

        setRecentSearches(prevSearches => {
            const filteredSearches = prevSearches.filter(
                search => search.query.toLowerCase() !== query.toLowerCase()
            );

            const newSearch: RecentSearch = {
                query: query.trim(),
                timestamp: Date.now()
            };

            const updatedSearches = [newSearch, ...filteredSearches].slice(0, MAX_RECENT_SEARCHES);
            
            saveToStorage(updatedSearches);
            
            return updatedSearches;
        });
    }, [saveToStorage]);

    const clearRecentSearches = useCallback(() => {
        setRecentSearches([]);
        localStorage.removeItem(RECENT_SEARCHES_KEY);
    }, []);

    const removeRecentSearch = useCallback((queryToRemove: string) => {
        setRecentSearches(prevSearches => {
            const updatedSearches = prevSearches.filter(
                search => search.query !== queryToRemove
            );
            saveToStorage(updatedSearches);
            return updatedSearches;
        });
    }, [saveToStorage]);

    return {
        recentSearches,
        addRecentSearch,
        clearRecentSearches,
        removeRecentSearch
    };
};
